import {
  Authenticator,
  ThemeProvider,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { Toaster, toast } from "@indigo-components";
import { type SignInInput, type SignInOutput, signIn } from "aws-amplify/auth";
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "@aws-amplify/ui-react/styles.css";
import { expertImage } from "../../assets/illustrations";
import AuthFlowHeader from "./AuthFlowHeader";
import "./DesignSystemSignIn.css";
import "./LoginPage.css";
import { authTheme } from "./amplifyAuthTheme";
import SignInFooter from "./SignInFooter";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authStatus } = useAuthenticator((context) => [
    context.user,
    context.authStatus,
  ]);

  // Retain the credentials the user just entered so the SMS "Resend Code"
  // button can request a fresh code without making them retype their password.
  // In-memory only for the duration of the sign-in attempt — never persisted.
  const credentialsRef = useRef<{ username: string; password: string } | null>(
    null,
  );

  // Amplify's built-in SMS "Resend Code" is broken for our password + SMS-MFA
  // flow: it re-calls signIn with authFlowType USER_AUTH and no password, which
  // (a) fails with "password is required to signIn" and (b) tears down the
  // in-flight SMS challenge, so the next confirm throws "signIn was not called
  // before confirmSignIn". We override handleSignIn to capture the password on
  // the initial sign-in and rewrite the resend into a real
  // signIn({ username, password }) that issues a fresh SMS code.
  const services = useMemo(
    () => ({
      async handleSignIn(input: SignInInput): Promise<SignInOutput> {
        // We infer "this is the SMS resend" from Amplify's current
        // resendSignInCode implementation (USER_AUTH + no password). Only the
        // SMS-OPTIONAL flow is enabled, so nothing else hits this shape today.
        // TODO: revisit on an Amplify upgrade, or if we ever enable a
        // passwordless / passkey flow that legitimately calls signIn with
        // USER_AUTH and no password.
        const isBrokenResend =
          input.options?.authFlowType === "USER_AUTH" && !input.password;

        if (isBrokenResend) {
          const saved = credentialsRef.current;
          if (saved && saved.username === input.username) {
            const output = await signIn({
              username: saved.username,
              password: saved.password,
            });
            toast.success("A new code has been sent to your phone.");
            return output;
          }
          // Retained password is gone (e.g. the page was reloaded mid-flow),
          // so we can't request a new code in place. Surface a clear message
          // — the machine shows it on the code screen — instead of Amplify's
          // cryptic "password is required to signIn" / "signIn was not called"
          // error, and let the user restart from the sign-in form.
          throw new Error("Your session expired. Please sign in again.");
        }

        if (input.username && input.password) {
          credentialsRef.current = {
            username: input.username,
            password: input.password,
          };
        }
        return signIn(input);
      },
    }),
    [],
  );

  // Drop the retained password when leaving the login page, so it doesn't
  // outlive the sign-in attempt if the user navigates away without completing.
  useEffect(
    () => () => {
      credentialsRef.current = null;
    },
    [],
  );

  // Check if the user is authenticated and redirect to the home page or the return URL
  useEffect(() => {
    if (authStatus === "authenticated" && user) {
      // Signed in — the retained password is no longer needed.
      credentialsRef.current = null;
      // Get the return URL from query parameters
      const searchParams = new URLSearchParams(location.search);
      const returnUrl = searchParams.get("returnUrl");

      // If we have a return URL, decode and use it
      if (returnUrl) {
        const decodedUrl = decodeURIComponent(returnUrl);
        navigate(decodedUrl, { replace: true });
      } else {
        // Otherwise, redirect to the home page, which will figure out
        // where to send the user based on their user group.
        navigate("/", { replace: true });
      }
    }
  }, [authStatus, user, navigate, location.search]);

  // Render login page with two-column layout when design system is enabled
  return (
    <div className={"login-page-container"}>
      {/* Login is a public route outside <App/>, so it needs its own Toaster
          for the resend-code confirmation to render. */}
      <Toaster />
      {/* Form side */}
      <div className={"login-form-side"}>
        <ThemeProvider theme={authTheme}>
          <Authenticator
            hideSignUp={true}
            services={services}
            components={{
              SignIn: {
                Header: () => <AuthFlowHeader heading="Welcome back!" />,
                Footer: SignInFooter,
              },
              ForceNewPassword: {
                Header: () => (
                  <AuthFlowHeader heading="Set your new password" />
                ),
              },
              ForgotPassword: {
                Header: () => <AuthFlowHeader heading="Reset your password" />,
              },
              ConfirmResetPassword: {
                Header: () => <AuthFlowHeader heading="Create new password" />,
              },
              ConfirmSignIn: {
                Header: () => (
                  <AuthFlowHeader heading="Enter verification code" />
                ),
              },
              VerifyUser: {
                Header: () => <AuthFlowHeader heading="Verify your identity" />,
              },
              ConfirmVerifyUser: {
                Header: () => (
                  <AuthFlowHeader heading="Confirm your identity" />
                ),
              },
            }}
          />
        </ThemeProvider>
      </div>

      {/* Illustration side - only shown when design system is enabled */}
      <div className="login-illustration-side">
        <div className="design-system-signin-glasses">
          <img
            src={expertImage}
            alt="Expert illustration"
            width="346"
            height="346"
            style={{ maxWidth: "100%", height: "auto" }}
          />
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
